import { CustomFlowbiteTheme } from 'flowbite-react';
import { DateInputTextSize } from '../../components/date-picker/date-picker';

/**
 *
 * @param hideCalendarIcon boolean
 * @param inputTextSize 'xs' | 'sm' (default)
 * @returns CustomFlowbiteTheme['datepicker']
 * generates a custom flowbite theme where some styles vary depending on component props values:
 * root.input.field.icon, root.input.field.input.withIcon.on
 */

export const getDatePickerTheme = (
    hideCalendarIcon: boolean,
    inputTextSize: DateInputTextSize,
): CustomFlowbiteTheme['datepicker'] => {
    const itemStyles = {
        base: 'block flex-1 cursor-pointer rounded-lg text-center text-sm font-semibold leading-9 text-system-primary bg-system-surface border border-transparent hover:bg-system-surface-container active:border-system-primary active:text-system-primary',
        selected: 'bg-system-secondary text-system-on-secondary hover:bg-system-secondary hover:border-transparent',
        disabled: 'text-gray-500',
    };

    const dynamicTheme = {
        root: {
            base: 'relative',
            /* using interface FlowbiteTextInputTheme: to remove a border and more */
            input: {
                base: 'rounded-md px-0 py-0',
                field: {
                    base: '',
                    input: {
                        base: 'w-full focus:border-system-outline-variant focus:ring-0 focus:outline-none',
                        colors: {
                            /* elements: text | bg | border | placeholder */
                            gray:
                                // rest
                                'bg-system-surface-container text-system-primary border-system-outline-variant' +
                                // focus, active
                                '',
                        },
                        withIcon: {
                            on: `py-2 ${hideCalendarIcon ? 'pl-2' : 'pl-10'} text-${inputTextSize}`, // dynamic
                            off: '',
                        },
                    }, // combine field.input.withIcon.on and field.icon styles
                    ...(hideCalendarIcon ? { icon: { base: 'hidden' } } : {}), // dynamic
                },
            },
        },
        popup: {
            root: {
                base: 'absolute top-10 z-50 block mt-1',
                inline: 'relative top-0 z-auto',
                inner: 'inline-block rounded-lg p-4 bg-system-surface border-none shadow-md', // edit bg, border
            },
            header: {
                base: 'text-system-primary',
                title: 'px-2 py-3 text-center font-semibold',
                selectors: {
                    base: 'mb-2 flex justify-between',
                    button: {
                        base: 'rounded-lg bg-system-surface border border-transparent hover:bg-system-surface-container px-5 py-2.5 text-sm font-semibold focus:outline-none focus:ring-0', // bg edit, hover:bg etc.
                        prev: '',
                        next: '',
                        view: '',
                    },
                },
            },
            view: {
                base: 'p-1 border-none focus:ring-0 focus:outline-none',
            },
            footer: {
                base: 'mt-2 flex space-x-2',
                button: {
                    base: 'w-full rounded-lg px-5 py-2 text-center text-sm font-medium focus:ring-0',
                    today: 'bg-system-secondary text-system-on-secondary',
                    clear: 'border border-system-outline-variant bg-system-surface text-system-primary',
                },
            },
        },
        views: {
            days: {
                header: {
                    base: 'mb-1 grid grid-cols-7',
                    title: 'h-6 text-center text-sm font-medium leading-6 text-system-on-surface-variant',
                },
                items: {
                    base: 'grid w-64 grid-cols-7',
                    item: itemStyles,
                },
            },
            months: {
                items: {
                    base: 'grid w-64 grid-cols-4',
                    item: itemStyles,
                },
            },
            years: {
                items: {
                    base: 'grid w-64 grid-cols-4',
                    item: itemStyles,
                },
            },
            decades: {
                items: {
                    base: 'grid w-64 grid-cols-4',
                    item: itemStyles,
                },
            },
        },
    };

    return dynamicTheme;
};
